import { useMemo, useState } from "react";
import { Button, DatePicker, Spin, message } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { Column, Line, Pie} from "@ant-design/plots";
import { fetchConversionPlans, fetchCourtesiesFunnel, fetchCourtesyPaymentType, fetchTimeToConversion } from "../../apis/statisticsApi";



dayjs.extend(utc);

// TODO: Refactor charts into separate components

const CourtesiesStatistics = () => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [funnel, setFunnel] = useState([]);
  const [conversionPlans, setConversionPlans] = useState([]);
  const [timeToConversion, setTimeToConversion] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);

  const STAGE_ORDER = ["Scheduled", "Attended", "Converted"];

  const funnelChartData = useMemo(() => {
    return funnel.flatMap((item) => [
      {
        period: item.period,
        stage: "Scheduled",
        value: item.scheduled,
      },
      {
        period: item.period,
        stage: "Attended",
        value: item.attended,
      },
      {
        period: item.period,
        stage: "Converted",
        value: item.converted,
      },
    ]).sort(
      (a, b) =>
        STAGE_ORDER.indexOf(a.stage) - STAGE_ORDER.indexOf(b.stage)
    );
  }, [funnel]);

  const conversionRateData = useMemo(() => {
    if (!funnel) return [];

    return funnel.flatMap((row) => [
      {
        period: row.period,
        rate: row.rate_scheduled,
        type: "Converted / Scheduled",
      },
      {
        period: row.period,
        rate: row.rate_attended,
        type: "Converted / Attended",
      },
    ]);
  }, [funnel]);

  const conversionPlansData = useMemo(() => {
    if (!conversionPlans?.length) return [];

    const totalUsers = conversionPlans.reduce(
      (sum, item) => sum + item.users,
      0
    );

    return conversionPlans
      .map((item) => ({
        plan: item.plan,
        percentage: Number(((item.users / totalUsers) * 100).toFixed(2)),
        users: item.users, // optional, for tooltip
      }))
      .sort((a, b) => b.percentage - a.percentage);
  }, [conversionPlans]);

  const BUCKET_ORDER = (bucket) => {
    if (bucket === "< 1 hour") return 0;
    if (bucket === "< 24 hours") return 1;
    return 2 + parseInt(bucket); // "X days"
  };

  const totalConversions = useMemo(() => {
    return timeToConversion.reduce(
      (sum, item) => sum + item.value,
      0
    );
  }, [timeToConversion]);

  const timeToConversionChartData = useMemo(() => {
    return [...timeToConversion]
      .sort((a, b) => BUCKET_ORDER(a.bucket) - BUCKET_ORDER(b.bucket))
      .map(item => ({
        bucket: item.bucket,
        value: item.value,
        percentage: totalConversions
          ? (item.value / totalConversions) * 100
          : 0,
      }));
  }, [timeToConversion, totalConversions]);

  const paymentTypeChartData = useMemo(() => {
    if (!paymentTypes?.length) return [];

    const total = paymentTypes.reduce(
      (sum, item) => sum + item.users,
      0
    );

    return paymentTypes
      .map(item => ({
        type: item.payment_type,
        percentage: total
          ? Number(((item.users / total) * 100).toFixed(2))
          : 0,
      }))
      .sort((a, b) => b.percentage - a.percentage);
  }, [paymentTypes]);

  const fetchStats = async () => {
    if (!startDate || !endDate) {
      message.warning("Por favor selecciona ambas fechas.");
      return;
    }

    const start = dayjs.utc(startDate);
    const end = dayjs.utc(endDate);

    if (!start.isBefore(end, "day")) {
      message.warning("La fecha de inicio debe ser anterior a la fecha de fin.");
      return;
    }

    setLoading(true);

    try {
      const [statisticsRes, conversionPlansRes, timeToConversionRes, paymentTypesRes] = await Promise.all([
        fetchCourtesiesFunnel(start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD")),
        fetchConversionPlans(start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD")),
        fetchTimeToConversion(start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD")),
        fetchCourtesyPaymentType(start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD")),
      ]);
      setFunnel(statisticsRes.data);
      setConversionPlans(conversionPlansRes.data);
      setTimeToConversion(timeToConversionRes.data);
      setPaymentTypes(paymentTypesRes.data);
    } catch (err) {
      console.error(err);
      message.error("Error loading courtesies statistics.");
    } finally {
      setLoading(false);
    }
  };

  const funnelConfig = {
    data: funnelChartData,
    xField: "period",
    yField: "value",
    group: true,
    colorField: 'stage',
    label: {
      layout: [
        { type: "interval-adjust-position" },
        { type: "interval-hide-overlap" },
      ],
    },
    interaction: {
      elementHighlightByColor: { link: true },
    },

    state: {
      active: { linkFill: 'rgba(0,0,0,0.25)', stroke: 'black', lineWidth: 0.5 },
      inactive: {
        opacity: 0.8 
      },
    },
  };

  const conversionRateConfig = {
    data: conversionRateData,
    xField: "period",
    yField: "rate",
    colorField: "type",
    smooth: true,
    yAxis: {
      label: {
        formatter: (v) => `${(v * 100).toFixed(0)}%`,
      },
    },
  };

  const selectedPlansConfig = {
    data: conversionPlansData,
    xField: "plan",
    yField: "percentage",
  };

const timeToConversionConfig = {
    data: timeToConversionChartData,
    xField: "bucket",
    yField: "percentage",
    loading,
    xAxis: {
      title: {
        text: "Time to conversion",
      },
    },
    yAxis: {
      title: {
        text: "Converted users",
      },
    },
    meta: {
      bucket: {
        alias: "Time to conversion",
      },
      value: {
        alias: "Users",
      },
    },
  };

  const paymentTypeConfig = {
    data: paymentTypeChartData,
    colorField: "type",
    angleField: "percentage",
    legend: false,
    label: {
      text: ({ type, percentage }) => {
        return `${type}: ${percentage}%`;
      },
      fill: '#fff',
      fontSize: 18,
    },
    style: {
      fill: ({ type }) => {
        if (type === 'Automatic Debit') {
          return 'blue';
        }
        return 'red';
      },
    },
  };

  return (
    <div className="container">
      <h2 className="text-center mb-3">Membership Distribution</h2>

      <div className="text-center" style={{ marginBottom: 16 }}>
        <DatePicker onChange={setStartDate} />{" "}
        <DatePicker onChange={setEndDate} />{" "}
        <Button onClick={fetchStats} type="primary">
          Buscar
        </Button>
      </div>

      {loading ? (
        <Spin />
      ) : (
        <>
        <div className="bg-white p-4 rounded-2xl shadow">
          <h3 className="mb-4 text-lg font-semibold">
            Courtesy Funnel
          </h3>

          {funnel.length === 0 ? (
            <p className="text-center">No data available</p>
          ) : (
            <Column {...funnelConfig} />
          )}
        </div>
        
        <div className="bg-white p-4 rounded-2xl shadow">
          <h3 className="mb-4 text-lg font-semibold">
            Courtesy Conversion
          </h3>

          {funnel.length === 0 ? (
            <p className="text-center">No data available</p>
          ) : (
            <Line {...conversionRateConfig} />
          )}
        </div>

        <div className="bg-white p-4 rounded-2xl shadow">
          <h3 className="mb-4 text-lg font-semibold">
            Conversion Plans
          </h3>

          {funnel.length === 0 ? (
            <p className="text-center">No data available</p>
          ) : (
            <Column {...selectedPlansConfig} />
          )}
        </div>
        <div className="bg-white p-4 rounded-2xl shadow">
          <h3 className="mb-4 text-lg font-semibold">
            Time To Conversion
          </h3>

          {funnel.length === 0 ? (
            <p className="text-center">No data available</p>
          ) : (
            <Column {...timeToConversionConfig} />
          )}
        </div>

        <div className="bg-white p-4 rounded-2xl shadow">
          <h3 className="mb-4 text-lg font-semibold">
            Automatic Debit vs Unique Payment
          </h3>

          {paymentTypeChartData.length === 0 ? (
            <p className="text-center">No data available</p>
          ) : (
            <Pie {...paymentTypeConfig} />
          )}
        </div>
        </>
      )}
    </div>
  );
};

export default CourtesiesStatistics;
